import { t } from 'services/i18n.service';

export const DEFAULT_TOP_OUTCOME_LEVEL = 5;
export const DEFAULT_TOP_PERSPIRATION_LEVEL = 2;

type TopMetricStep = {
  min: number;
  label: string;
};

const resolveMetricLabel = (steps: TopMetricStep[], value: number): string => {
  const clamped = Math.max(1, Math.min(10, Math.round(value || 0)));
  let label = steps[0]?.label ?? '';

  for (const step of steps) {
    if (clamped >= step.min) {
      label = step.label;
    } else {
      break;
    }
  }

  return label;
};

export const getDefaultTopMetrics = () => ({
  outcome: {
    value: DEFAULT_TOP_OUTCOME_LEVEL,
    label: resolveMetricLabel(getOutcomeSteps(), DEFAULT_TOP_OUTCOME_LEVEL)
  },
  perspiration: {
    value: DEFAULT_TOP_PERSPIRATION_LEVEL,
    label: resolveMetricLabel(
      getPerspirationSteps(),
      DEFAULT_TOP_PERSPIRATION_LEVEL
    )
  }
});

/**
 * Returns outcome step options with labels resolved at call time so they
 * always reflect the currently active language.
 */
export const getOutcomeSteps = () => [
  { min: 1, label: t('todos.who_cares') },
  { min: 2, label: t('todos.kind_of_important') },
  { min: 4, label: t('todos.big_deal') },
  { min: 6, label: t('todos.huge') },
  { min: 8, label: t('todos.do_it_now') },
  { min: 9, label: t('todos.mind_blowing') }
];

/**
 * Returns perspiration step options with labels resolved at call time so they
 * always reflect the currently active language.
 */
export const getPerspirationSteps = () => [
  { min: 1, label: t('todos.five_minutes_job') },
  { min: 2, label: t('todos.fifteen_minutes_work') },
  { min: 3, label: t('todos.half_an_hour') },
  { min: 4, label: t('todos.an_hour') },
  { min: 5, label: t('todos.half_a_day') },
  { min: 7, label: t('todos.a_day') },
  { min: 9, label: t('todos.a_week') }
];

export const RANGE_SLIDER_STEPS = {
  MIN: 1,
  MAX: 10,
  STEP: 1
};

export type TopDateOption = {
  label: string;
  value: string;
  getDate: () => Date;
};

/**
 * Returns date preset options with labels resolved at call time so they
 * always reflect the currently active language.
 */
export const getDateOptions = (): TopDateOption[] => [
  {
    label: t('todos.today'),
    value: 'today',
    getDate: () => new Date()
  },
  {
    label: t('todos.tomorrow'),
    value: 'tomorrow',
    getDate: () => {
      const date = new Date();
      date.setDate(date.getDate() + 1);
      return date;
    }
  },
  {
    label: t('todos.in_a_few_days'),
    value: 'few_days',
    getDate: () => {
      const date = new Date();
      date.setDate(date.getDate() + 3);
      return date;
    }
  },
  {
    label: t('todos.next_week'),
    value: 'next_week',
    getDate: () => {
      const date = new Date();
      date.setDate(date.getDate() + 7);
      return date;
    }
  },
  {
    label: t('todos.in_two_weeks'),
    value: 'two_weeks',
    getDate: () => {
      const date = new Date();
      date.setDate(date.getDate() + 14);
      return date;
    }
  },
  {
    label: t('todos.in_a_month'),
    value: 'month',
    getDate: () => {
      const date = new Date();
      date.setMonth(date.getMonth() + 1);
      return date;
    }
  },
  {
    label: t('todos.in_a_few_months'),
    value: 'few_months',
    getDate: () => {
      const date = new Date();
      date.setMonth(date.getMonth() + 3);
      return date;
    }
  },
  {
    label: t('todos.in_a_year'),
    value: 'year',
    getDate: () => {
      const date = new Date();
      date.setFullYear(date.getFullYear() + 1);
      return date;
    }
  },
  {
    label: t('todos.hah_ill_never_actually_do_this'),
    value: 'never',
    getDate: () => {
      const date = new Date();
      date.setDate(date.getDate() + 365 * 10); // 10 years in the future
      return date;
    }
  }
];
